import React, { useMemo, useRef, useState } from 'react';
import {
  Card,
  CardBody,
  Button,
  Input,
  Label,
  FormGroup,
  Progress,
  Row,
  Col,
  Alert,
} from 'reactstrap';
import documentsApi, { ALLOWED_MIME_TYPES, DEFAULT_MAX_FILE_SIZE_MB } from 'service/documentsApi';

const inferContextDefaults = (pathname) => {
  const isMerchant = /\/merchants\b/.test(pathname);
  const isApplication = /\/applications\b/.test(pathname);
  if (isMerchant) return { originType: 'merchant', category: 'merchant' };
  if (isApplication) return { originType: 'application', category: 'application' };
  return { originType: 'merchant', category: 'merchant' };
};

const toChipsArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  // No dividir por comas, solo entrada completa (se agrega con Enter o debounce)
  return [String(value).trim()].filter(Boolean);
};

const DocumentUpload = ({
  originId,
  originType: originTypeProp,
  category: categoryProp,
  onUploaded,
  accept,
  maxSizeMB,
  disabled,
  className,
}) => {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const defaults = useMemo(() => inferContextDefaults(pathname), [pathname]);

  const [file, setFile] = useState(null);
  const [description, setDescription] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [tags, setTags] = useState([]);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const abortRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const finalAccept = accept || ALLOWED_MIME_TYPES.join(',');
  const finalMaxMB = maxSizeMB || DEFAULT_MAX_FILE_SIZE_MB;
  const finalOriginType = originTypeProp || defaults.originType;
  const finalCategory = categoryProp || defaults.category;

  const reset = () => {
    setProgress(0);
    setIsUploading(false);
    if (abortRef.current) abortRef.current = null;
  };

  const handleBrowseClick = () => {
    if (disabled) return;
    inputRef.current && inputRef.current.click();
  };

  const handleFileChosen = (f) => {
    setError(null);
    setFile(f || null);
  };

  const onInputChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (f) handleFileChosen(f);
    // Permitir seleccionar el mismo archivo consecutivamente
    try { e.target.value = ''; } catch (_) {}
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    if (disabled) return;
    const f = e.dataTransfer.files && e.dataTransfer.files[0];
    if (f) handleFileChosen(f);
  };

  const onDragOver = (e) => {
    e.preventDefault();
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const next = toChipsArray(tagsInput);
      setTags((prev) => Array.from(new Set([...prev, ...next])));
      setTagsInput('');
    }
  };

  const handleBlurTags = () => {
    const next = toChipsArray(tagsInput);
    setTags((prev) => Array.from(new Set([...prev, ...next])));
    setTagsInput('');
  };

  // Auto-agregar tag 2s después de dejar de escribir
  const onTagsInputChange = (val) => {
    const trimmed = String(val).slice(0, 255);
    setTagsInput(trimmed);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const next = toChipsArray(trimmed);
      if (next.length > 0) {
        setTags((prev) => Array.from(new Set([...prev, ...next])));
        setTagsInput('');
      }
    }, 2000);
  };

  const cancelUpload = () => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
  };

  const startUpload = async () => {
    if (!file) {
      setError('Selecciona o arrastra un archivo para subir.');
      return;
    }

    setError(null);
    setIsUploading(true);
    setProgress(0);
    setAttempt((a) => a + 1);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const { documentId, details } = await documentsApi.uploadDocument({
        file,
        category: finalCategory,
        originType: finalOriginType,
        originId,
        description,
        tags,
        signal: controller.signal,
        maxSizeMB: finalMaxMB,
        onUploadProgress: (p) => setProgress(p.percent || 0),
      });

      reset();
      if (onUploaded) onUploaded({ documentId, details });
    } catch (err) {
      if (err && err.__aborted) {
        setError('Subida cancelada.');
      } else {
        setError(err?.message || 'Error al subir el documento.');
      }
      setIsUploading(false);
    }
  };

  return (
    <Card className={className}>
      <CardBody>
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          className="border rounded p-4 text-center"
          style={{ cursor: disabled ? 'not-allowed' : 'pointer', background: '#fafafa' }}
          onClick={handleBrowseClick}
          aria-label="Área para arrastrar y soltar archivos"
        >
          <input
            ref={inputRef}
            type="file"
            accept={finalAccept}
            onChange={onInputChange}
            style={{ display: 'none' }}
            disabled={disabled}
          />
          <div className="mb-2">
            <i className="ni ni-cloud-upload-96 text-primary" style={{ fontSize: '2rem' }} />
          </div>
          <div className="mb-2">
            Drag & drop a file here or
          </div>
          <Button color="primary" size="sm" disabled={disabled} onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleBrowseClick(); }} onMouseDown={(e) => e.preventDefault()}>
            Browse
          </Button>
          {file && (
            <div className="mt-3 text-muted">
              Selected: {file.name} ({Math.round(file.size / 1024)} KB)
            </div>
          )}
        </div>

        <Row className="mt-3">
          <Col xs="12" md="12" lg="8" className="mb-2 mb-md-0">
            <FormGroup>
              <Label>Description (optional)</Label>
              <Input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value.slice(0, 100))}
                disabled={disabled}
              />
            </FormGroup>
          </Col>
          <Col xs="12" md="12" lg="4">
            <FormGroup>
              <Label>Tags (press Enter)</Label>
              <Input
                type="text"
                value={tagsInput}
                onChange={(e) => onTagsInputChange(e.target.value)}
                onBlur={handleBlurTags}
                onKeyDown={handleAddTag}
                placeholder="Type a tag and press Enter"
                disabled={disabled}
              />
              <small className="d-block text-muted mt-1">You can press Enter to add a tag, or wait 2 seconds to add it automatically.</small>
            </FormGroup>
          </Col>
        </Row>

        {tags && tags.length > 0 && (
          <div className="mb-2 d-flex flex-wrap">
            {tags.map((t, idx) => (
              <div key={`${t}-${idx}`} className="d-inline-flex align-items-center mr-2 mb-2">
                <span className="badge badge-light" style={{ padding: '0.35rem 0.6rem', fontSize: '0.8rem', borderRadius: '12px', border: '1px solid #a5bcd1', color: '#4a6b8a' }}>{t}</span>
                <Button
                  color="link"
                  size="sm"
                  className="p-0 ml-1"
                  onClick={() => setTags((prev) => prev.filter((x, i) => i !== idx))}
                  aria-label={`Remove tag ${t}`}
                  title="Remove tag"
                >
                  <i className="ni ni-fat-remove text-danger" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {error && (
          <Alert color="danger" className="mt-2" role="alert">
            {error}
          </Alert>
        )}

        {isUploading && (
          <div className="mt-3">
            <Progress value={progress} max={100} />
            <div className="text-right text-muted" style={{ fontSize: 12 }}>{progress}%</div>
            <div className="d-flex mt-2">
              <Button color="warning" size="sm" className="mr-2" onClick={cancelUpload} aria-label="Cancel upload">
                Cancel
              </Button>
              <Button color="secondary" size="sm" disabled>
                Uploading...
              </Button>
            </div>
          </div>
        )}

        {!isUploading && (
          <div className="mt-2 d-flex">
            <Button color="success" size="sm" onClick={startUpload} disabled={disabled || !file} aria-label="Upload document" style={{ width: 40, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="ni ni-cloud-upload-96" />
            </Button>
            {attempt > 0 && (
              <div className="text-muted ml-3" style={{ lineHeight: '31px' }}>Attempt: {attempt}</div>
            )}
          </div>
        )}
        {/* Removed bottom helper text as requested */}
      </CardBody>
    </Card>
  );
};

export default DocumentUpload;



